declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: () => void;
          renderButton: (element: HTMLElement, config: any) => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

interface GoogleAuthResponse {
  credential: string;
}

class GoogleAuthService {
  private isInitialized = false;
  private clientId: string;

  constructor() {
    this.clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  }

  async loadGoogleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error('Failed to load Google Identity Services'));
      document.head.appendChild(script);
    });
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    if (!this.clientId) {
      console.warn(
        'Google Client ID not configured. Set REACT_APP_GOOGLE_CLIENT_ID environment variable.'
      );
      return;
    }

    try {
      await this.loadGoogleScript();

      window.google.accounts.id.initialize({
        client_id: this.clientId,
        callback: () => {}, // This will be overridden per use
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize Google Auth:', error);
      throw error;
    }
  }

  async signIn(): Promise<string> {
    await this.initialize();

    return new Promise((resolve, reject) => {
      if (!window.google?.accounts?.id) {
        reject(new Error('Google Identity Services not available'));
        return;
      }

      // Create a temporary callback for this sign-in attempt
      window.google.accounts.id.initialize({
        client_id: this.clientId,
        callback: (response: GoogleAuthResponse) => {
          if (response.credential) {
            resolve(response.credential);
          } else {
            reject(new Error('No credential received from Google'));
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      // Trigger the sign-in flow
      window.google.accounts.id.prompt();
    });
  }

  renderButton(
    element: HTMLElement,
    callback: (credential: string) => void
  ): void {
    if (!this.isInitialized || !window.google?.accounts?.id) {
      console.error('Google Auth not initialized');
      return;
    }

    // Set up the callback for this button
    window.google.accounts.id.initialize({
      client_id: this.clientId,
      callback: (response: GoogleAuthResponse) => {
        if (response.credential) {
          callback(response.credential);
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    window.google.accounts.id.renderButton(element, {
      theme: 'outline',
      size: 'large',
      width: '100%',
      text: 'signin_with',
      logo_alignment: 'left',
    });
  }

  signOut(): void {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  }
}

export const googleAuthService = new GoogleAuthService();
